import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { ContactFieldRowError } from "@/components/features/contact/ContactFieldRowError/ContactFieldRowError";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Label,
  SimpleSelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { ExpandableText } from "@/components/ui/ExpandableText/ExpandableText";
import { useDeleteContactWebsite } from "@/lib/data/react-query";
import { CONTACT_WEBSITE_LABELS } from "@/lib/data/static/contactStatics";
import { type ContactWebsite } from "@/lib/types/Contact";
import { ContactWebsiteIcon } from "@/lib/utils/iconUtils";
import { cn } from "@/lib/utils/cn";
import { Pencil, Plus, Trash2 } from "lucide-react";
import pluralize from "pluralize";
import { useState } from "react";
import {
  useFieldArray,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from "react-hook-form";
import { type z } from "zod";
import { AddOrEditContactWebsiteSlideover } from "./AddOrEditContactWebsiteSlideover";

const newClientRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function ContactWebsitesInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  compact,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  compact?: boolean;
}) {
  const { control, register } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "websites",
  });
  const watchedWebsites = useWatch({ control, name: "websites" });

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    remove(index);
  };

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }
    append({
      id: newClientRowId(),
      address: "",
      label: CONTACT_WEBSITE_LABELS[0],
      description: "",
      dateModified: "",
    });
  };

  const websiteCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;

  return (
    <div>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <ContactWebsiteIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Website", websiteCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full overflow-x-visible">
        {fields.length > 0 && (
          <ul className="space-y-3">
            {fields.map((field, index) => {
              const row = watchedWebsites?.[index];
              return (
                <li
                  key={field.id}
                  className={cn(
                    compact
                      ? "flex flex-col gap-1"
                      : "border-border space-y-3 rounded-md border p-3",
                  )}
                  data-testid={
                    row?.id ? `contact-website-item-${row.id}` : undefined
                  }
                >
                  <div
                    className={cn(
                      "flex min-w-0 flex-wrap sm:flex-nowrap",
                      compact
                        ? "items-stretch gap-2 sm:gap-3"
                        : "items-start gap-3",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <TextFormField
                        label={compact ? undefined : "Website URL"}
                        className={cn(
                          "w-full [&_input]:h-10",
                          compact && "!space-y-0 [&>div:first-child]:hidden",
                        )}
                        register={register(`websites.${index}.address`)}
                        placeholder="https://"
                        disabled={disabled}
                        dataTestId="contact-website-url-input"
                      />
                    </div>
                    <div
                      className={cn(
                        "min-w-0 sm:w-40",
                        compact ? "w-32" : "w-full",
                      )}
                    >
                      <SimpleSelectFormField
                        label={compact ? undefined : "Label"}
                        className="h-10 w-full min-w-0"
                        formItemClassName={cn(
                          "w-full min-w-0",
                          compact && "!m-0 !space-y-0",
                        )}
                        placeholder="Label"
                        register={register(`websites.${index}.label`)}
                        items={CONTACT_WEBSITE_LABELS as readonly string[]}
                        disabled={disabled}
                      />
                    </div>
                    <div
                      className={cn(
                        "flex shrink-0",
                        compact ? "items-center" : "pt-6",
                      )}
                    >
                      {hasDeletePermission && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          disabled={disabled}
                          onClick={() => {
                            handleRemove(index);
                          }}
                          aria-label="Remove website"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {!compact && (
                    <TextFormField
                      label="Description"
                      className="w-full"
                      register={register(`websites.${index}.description`)}
                      placeholder="Optional"
                      disabled={disabled}
                    />
                  )}
                  <ContactFieldRowError
                    control={parentForm.control}
                    fieldPath={
                      `websites.${index}.address` as FieldPath<
                        z.infer<typeof createContactFormSchema>
                      >
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <div className="mt-2 w-full self-start">
            <Button
              type="button"
              variant="link"
              className="contact-subitem-add text-muted-foreground h-auto min-h-0 justify-start p-0"
              onClick={addRow}
              data-testid="add-website-button"
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add website
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContactWebsitesSection({
  websites,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  compact,
  parentForm,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  websites?: ContactWebsite[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  compact?: boolean;
}) {
  const deleteContactWebsite = useDeleteContactWebsite();
  const [activeWebsiteId, setActiveWebsiteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (parentForm) {
    return (
      <ContactWebsitesInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
        compact={compact}
      />
    );
  }

  const websiteList = websites ?? [];
  const parseSortableDate = (value: unknown) =>
    typeof value === "string" ? Date.parse(value) : Number.NaN;
  const sortedWebsiteList = websiteList
    .map((website, index) => ({ website, index }))
    .sort((a, b) => {
      const aDate = parseSortableDate(a.website.dateModified);
      const bDate = parseSortableDate(b.website.dateModified);
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return aDate - bDate;
      }
      return a.index - b.index;
    })
    .map(({ website }) => website);
  const websiteCount = Math.max(sortedWebsiteList.length, 1);
  const showEmptyState = websiteList.length === 0 && !isAdding;

  const activeWebsite = (websites ?? []).find(
    (website) => website.id === activeWebsiteId,
  );

  const handleDelete = (website: Pick<ContactWebsite, "id">) => {
    deleteContactWebsite.mutate({
      id: website.id,
      contactId: contactId,
    });
    setActiveWebsiteId(null);
  };

  return (
    <div>
      {!hideLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactWebsiteIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Website", websiteCount)}
            </Label>
          </div>
          {hasCreatePermission && (
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-website-button"
            >
              Create website
            </Button>
          )}
        </div>
      )}
      <div className="col-span-full">
        {hideLabel && hasCreatePermission && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-website-button"
            >
              Create website
            </Button>
          </div>
        )}
        {isAdding && (
          <div className="border-border bg-card mt-3 w-full rounded-md border p-3">
            <AddOrEditContactWebsiteSlideover
              parentForm={parentForm}
              editing={false}
              setOpen={setIsAdding}
              open={isAdding}
              contactId={contactId}
              hasEditPermission={hasEditPermission}
              variant="inline"
            />
          </div>
        )}
        {showEmptyState ? (
          <Typography
            variant="subHeading"
            className="text-muted-foreground mt-3"
          >
            No websites found.
          </Typography>
        ) : (
          <ul className="mt-3 space-y-3">
            {sortedWebsiteList.map((website, index) => {
              const label = website.label?.trim();
              const description = website.description?.trim();
              const isActiveEdit = isEditing && website.id === activeWebsiteId;
              if (isActiveEdit) {
                return (
                  <li
                    key={website.id || `${website.address}-${index}`}
                    className="border-border bg-card w-full rounded-md border p-3"
                  >
                    <AddOrEditContactWebsiteSlideover
                      editing={true}
                      parentForm={parentForm}
                      activeContactWebsite={website}
                      setOpen={(nextOpen) => {
                        setIsEditing(nextOpen);
                        if (!nextOpen) {
                          setActiveWebsiteId(null);
                        }
                      }}
                      open={isEditing}
                      contactId={contactId}
                      hasEditPermission={hasEditPermission}
                      hasDeletePermission={hasDeletePermission}
                      onDelete={() => {
                        setIsDeleting(true);
                      }}
                      variant="inline"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={website.id || `${website.address}-${index}`}
                  className="border-border bg-card w-full rounded-md border p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Typography weight="medium" className="truncate">
                        {website.address || "-"}
                      </Typography>
                      {label && (
                        <Badge variant="secondary" className="h-6 shrink-0">
                          {label}
                        </Badge>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!website.id) {
                          return;
                        }
                        setIsEditing(true);
                        setActiveWebsiteId(website.id);
                      }}
                      disabled={!website.id}
                      aria-label="Edit website"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  {description && (
                    <div className="mt-2">
                      <ExpandableText
                        text={description}
                        maxLines={3}
                        size="sm"
                        className="text-muted-foreground"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this website?`}
        open={isDeleting && !!activeWebsite}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeWebsite) {
            handleDelete(activeWebsite);
          }
        }}
      />
    </div>
  );
}
